// @ts-check
// Regras puras do Embedded Signup da Meta (versão 4) e da volta do resultado para a janela da escola (contrato 08 de
// leads e WhatsApp, seção 8.1). Cada função é autocontida, sem import nem referência a outra coisa do módulo: a página
// do portal (src/integrations/meta-signup-page.js) embute o código delas por Function.prototype.toString(), e a SPA e
// os testes importam as mesmas funções.

// etapas que a Meta informa em CANCEL.current_step; os textos ficam no i18n de cada tela
export const EMBEDDED_SIGNUP_CANCEL_STEPS = Object.freeze([
  'BUSINESS_ACCOUNT_SELECTION', 'WABA_PHONE_PROFILE_PICKER', 'WHATSAPP_BUSINESS_PROFILE_SETUP', 'PHONE_NUMBER_SETUP', 'PHONE_NUMBER_VERIFICATION', 'PERMISSIONS',
]);
export const META_SIGNUP_RESULT_TYPE = 'appcrm.metaSignup';
// resposta da janela da escola ao READY do portal: só ela libera o login da Meta (aperto de mão)
export const META_SIGNUP_ACK_TYPE = 'appcrm.metaSignup.ack';
export const META_SIGNUP_MODES = Object.freeze(['NEW_NUMBER', 'BUSINESS_APP']);

/**
 * Só a Meta fala com a página: https e facebook.com ou um subdomínio dele (o `endsWith('facebook.com')` do exemplo da
 * Meta aceitaria evilfacebook.com). `extraOrigin` é a origem exata do simulador local, quando configurado.
 * @param {unknown} origin @param {string | null} [extraOrigin]
 */
export function isAllowedMetaOrigin(origin, extraOrigin) {
  if (typeof origin !== 'string' || !origin || origin === 'null') return false;
  if (extraOrigin && origin === extraOrigin) return true;
  var url;
  try {
    url = new URL(origin);
  } catch (error) {
    return false;
  }
  if (url.origin !== origin || url.protocol !== 'https:' || url.port !== '') return false;
  return url.hostname === 'facebook.com' || url.hostname.slice(-'.facebook.com'.length) === '.facebook.com';
}

/**
 * Mensagem da sessão do Embedded Signup (`type: 'WA_EMBEDDED_SIGNUP'`), em texto JSON ou objeto. Nulo para qualquer
 * outra mensagem. CANCEL com `error_message` é um erro relatado pela pessoa; ERROR também vira erro.
 * @param {unknown} data
 * @returns {null | { kind: 'FINISH', finishType: string, phoneNumberId: string | null, wabaId: string | null, businessId: string | null }
 *   | { kind: 'CANCEL', currentStep: string | null }
 *   | { kind: 'ERROR', errorMessage: string, errorCode: string | null, sessionId: string | null }}
 */
export function parseEmbeddedSignupEvent(data) {
  var message = data;
  if (typeof message === 'string') {
    try {
      message = JSON.parse(message);
    } catch (error) {
      return null;
    }
  }
  if (!message || typeof message !== 'object' || /** @type {any} */ (message).type !== 'WA_EMBEDDED_SIGNUP') return null;
  var parsed = /** @type {any} */ (message);
  var details = parsed.data && typeof parsed.data === 'object' ? parsed.data : {};
  var text = function (/** @type {unknown} */ value) {
    return typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
  };
  var finishTypes = ['FINISH', 'FINISH_ONLY_WABA', 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING', 'FINISH_OBO_MIGRATION', 'FINISH_GRANT_ONLY_API_ACCESS'];
  var event = text(parsed.event);
  if (finishTypes.indexOf(event) >= 0) {
    return {
      kind: 'FINISH', finishType: event, phoneNumberId: text(details.phone_number_id) || null, wabaId: text(details.waba_id) || null,
      businessId: text(details.business_id) || null,
    };
  }
  if (event === 'CANCEL' && !text(details.error_message)) return { kind: 'CANCEL', currentStep: text(details.current_step) || null };
  if (event === 'CANCEL' || event === 'ERROR') {
    return {
      kind: 'ERROR', errorMessage: text(details.error_message) || text(details.message), errorCode: text(details.error_code) || null,
      sessionId: text(details.session_id) || null,
    };
  }
  return null;
}

/**
 * Opções do FB.login da versão 4: `extras` vazio (a versão vem da configuração do Facebook Login for Business) e, para o
 * número que continua no app WhatsApp Business (coexistência), só o `featureType`.
 * @param {{ configId: string, mode: string }} input
 */
export function buildEmbeddedSignupLoginOptions(input) {
  return {
    config_id: input.configId,
    response_type: 'code',
    override_default_response_type: true,
    extras: input.mode === 'BUSINESS_APP' ? { featureType: 'whatsapp_business_app_onboarding' } : {},
  };
}

/**
 * A janela da escola só aceita o resultado do portal: mesma origem, do popup que ela abriu e da tentativa atual.
 * @param {{ origin?: string, source?: unknown, data?: any }} event
 * @param {{ portalOrigin: string, popup: unknown, attemptId: string }} expected
 */
export function isMetaSignupResultMessage(event, expected) {
  if (!event || !expected || !expected.portalOrigin || !expected.attemptId) return false;
  if (event.origin !== expected.portalOrigin || !expected.popup || event.source !== expected.popup) return false;
  var data = event.data;
  return Boolean(data) && typeof data === 'object' && data.type === 'appcrm.metaSignup' && data.attemptId === expected.attemptId;
}

/**
 * Portal: a resposta da janela da escola ao READY, que libera "Entrar com a Meta". Vale só com a origem exata da escola
 * guardada no passe, vinda da janela que abriu o popup e da tentativa do passe, com a prova (64 caracteres
 * hexadecimais) que a escola recebeu na criação do passe. Um link aberto noutro navegador, por outra pessoa ou a partir
 * de outro site nunca recebe essa resposta.
 * @param {{ origin?: string, source?: unknown, data?: any }} event
 * @param {{ tenantOrigin: string, opener: unknown, attemptId: string }} expected
 */
export function isMetaSignupAckMessage(event, expected) {
  if (!event || !expected || !expected.tenantOrigin || !expected.attemptId || !expected.opener) return false;
  if (event.origin !== expected.tenantOrigin || event.source !== expected.opener) return false;
  var data = event.data;
  return Boolean(data) && typeof data === 'object' && data.type === 'appcrm.metaSignup.ack' && data.attemptId === expected.attemptId
    && typeof data.proof === 'string' && /^[0-9a-f]{64}$/.test(data.proof);
}

/**
 * Passe no fragmento da página do portal (`#pass=<token>`); vazio quando ausente ou mal formado.
 * @param {unknown} hash
 */
export function readPassFromHash(hash) {
  if (typeof hash !== 'string') return '';
  var parameters = new URLSearchParams(hash.charAt(0) === '#' ? hash.slice(1) : hash);
  var pass = parameters.get('pass') || '';
  return /^[A-Za-z0-9_-]{32,128}$/.test(pass) ? pass : '';
}
